import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Card, Spinner, Image } from "react-bootstrap";
import Swal from "sweetalert2";
import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { useBusiness } from "../../contexts/BusinessContext";
import { allowOnlyNumbers } from "../../utils/AllowOnlyNumbers";
import TextareaInput from "../components/TextareaInput";
import AlertService from "../../services/AlertService";

const EditItem: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { business } = useBusiness();
  const [businessItemId] = useState<any>(state?.businessItemId);

  const [itemData, setItemData] = useState<any>(null);
  const [hours, setHours] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);

  // ✅ Fetch item data
  useEffect(() => {
    if (!businessItemId) return;

    const fetchItemData = async () => {
      setLoading(true);
      try {
        const res: any = await _post(apiUrls.getItem, { business_item_id: businessItemId });

        if (res.data?.success) {
          const data = res.data.data;
          setItemData(data);
          console.log(data)

          // Parse duration if it’s in HH:MM format
          if (data.duration && typeof data.duration === "string") {
            const [h, m] = data.duration.split(":").map(Number);
            setHours(h || 0);
            setMinutes(m || 0);
          }

          // Existing images
          if (Array.isArray(data.images)) {
            setExistingImages(data.images);
          }
        } else {
          Swal.fire("Error", res.data?.message || "Failed to fetch item data.", "error");
        }
      } catch (err) {
        Swal.fire("Error", "An error occurred while fetching item data.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchItemData();
  }, [businessItemId]);

  // ✅ Handle file uploads
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setNewImages((prev) => [...prev, ...Array.from(files)]);
    }
  };

  const handleRemoveImage = async (index: number, isExisting: boolean) => {
    if (isExisting) {
      const deleteImage:any = existingImages[index];
      
      if(deleteImage.is_default){
        AlertService.warning('Default image can not be deleted!');
        return;
      }

      const deleteExistingImage = await _post(apiUrls.deleteItemImage, {business_item_image_id: deleteImage?.id});

      if(deleteExistingImage?.data?.success){
        setExistingImages((prev) => prev.filter((_, i) => i !== index));
        await Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Image has been deleted successfully.",
          timer: 1800,
          showConfirmButton: false,
        });
      }
    }
    else setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  // ✅ Submit updated form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemData || !business?.biz_code) {
      Swal.fire("Error", "Missing business or item data.", "error");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("business_item_id", String(itemData.id));
      formData.append("biz_code", business.biz_code);
      formData.append("price", String(itemData.price || ""));
      formData.append("duration", `${hours}:${minutes}`);
      formData.append("description", itemData.description || "");

      newImages.forEach((file) => formData.append("images[]", file));

      const res: any = await _post(apiUrls.updateItem, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        await Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Item has been updated successfully.",
          timer: 1800,
          showConfirmButton: false,
        });
        navigate(-1);
      } else {
        Swal.fire("Failed!", res.data?.message || "Item update failed.", "error");
      }
    } catch (err) {
      Swal.fire("Error", "An error occurred while updating item.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!itemData) {
    return (
      <div className="text-center mt-5">
        {loading ? <Spinner animation="border" /> : <p>No item found.</p>}
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  // ✅ Main render
  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        {itemData.name_en || itemData.item?.name_en}:{" "}
        <span className="text-secondary">update info</span>
        <Button className="float-end" variant="dark" size="sm" onClick={() => navigate(-1)}>
          Back
        </Button>
      </h1>

      <Card className="d-shadow">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <div className="row">
              {/* LEFT SIDE */}
              <div className="col-sm-6">
                <div className="row">
                  <div className="col-sm-6">
                    <label className="control-label mb-2">Duration</label>
                    <div className="input-group mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="--"
                        value={hours}
                        onKeyDown={allowOnlyNumbers}
                        onChange={(e) => setHours(Number(e.target.value))}
                      />
                      <span className="input-group-text">hr</span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="--"
                        value={minutes}
                        onKeyDown={allowOnlyNumbers}
                        onChange={(e) => setMinutes(Number(e.target.value))}
                      />
                      <span className="input-group-text">min</span>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <label className="control-label mb-2">Price</label>
                    <div className="input-group mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="--"
                        value={itemData.price || ""}
                        onKeyDown={allowOnlyNumbers}
                        onChange={(e) =>
                          setItemData({ ...itemData, price: e.target.value })
                        }
                      />
                      <span className="input-group-text">BDT</span>
                    </div>
                  </div>
                </div>

                <TextareaInput
                  label="Description"
                  name="description"
                  value={itemData.description || ""}
                  onChange={(name, value) =>
                    setItemData({ ...itemData, [name]: value })
                  }
                />
              </div>

              {/* RIGHT SIDE */}
              <div className="col-sm-6">
                <Form.Group>
                  <Form.Label>Images <span className="text-danger">(square shape)</span></Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        document.getElementById("fileUploadInput")?.click()
                      }
                    >
                      <i className="bi bi-upload me-1"></i> Upload Images
                    </Button>
                    <span className="text-muted small">
                      {newImages.length + existingImages.length} selected
                    </span>
                  </div>
                  <input
                    id="fileUploadInput"
                    type="file"
                    multiple
                    accept="image/*"
                    className="d-none"
                    onChange={handleImageUpload}
                  />
                </Form.Group>

                <div className="row mt-3">
                  {existingImages.map((img:any, idx) => (
                    <div key={`exist-${idx}`} className="col-4 mb-3 position-relative">
                      <Image
                        src={img?.image_url}
                        thumbnail
                        style={{ width: "100%", height: "100px", objectFit: "cover" }}
                      />
                      {!img?.is_default && (
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => handleRemoveImage(idx, true)}
                        >
                          X
                        </Button>
                      )}
                    </div>
                  ))}
                  {newImages.map((file, idx) => (
                    <div key={`new-${idx}`} className="col-4 mb-3 position-relative">
                      <Image
                        src={URL.createObjectURL(file)}
                        thumbnail
                        style={{ width: "100%", height: "100px", objectFit: "cover" }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0"
                        onClick={() => handleRemoveImage(idx, false)}
                      >
                        X
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-end mt-4">
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
                className="me-2"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" /> Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EditItem;
